import { femaleQuestionMedium, maleQuestionMedium, bulbMedium } from '@/assets/icons';
import { WITHOUT_DATA, WITHOUT_DATA_SOURCE } from './quotes';

const INDICATORS = `
<p>
  Le management de la performance par l'amélioration continue doit être centrée sur le client. Le besoin du client doit être la priorité absolue de la création de valeur de l'entreprise.
  Ce client peut être l'entité à laquelle on vend un produit, à l'issue d'une transaction, mais on peut également parler de client interne, c'est-à-dire une autre équipe ou entité au sein de la même entreprise.<br>
  Le management de la performance repose sur des indicateurs forts qui permettent à l'équipe de contrôler ses performances et de mettre en place les actions requises en cas d'écart à l'objectif.
</p>
<h2 class="basic-sub-title">
  Qu'est-ce qu'un indicateur ?
</h2>
<div class="ia-sensei-quote">
  <span class="quote">"${WITHOUT_DATA}"</span>
  <br>
  <span class="author">${WITHOUT_DATA_SOURCE}</span>
</div>
<p>
  Aujourd'hui, trop de décisions sont prises sur la base d'opinions, un bon management de la performance éradique les suppositions et les remplacent par des données tangibles.
  La mesure permet donc de collecter la donnée et d'avoir un regard objectif sur un processus.
</p>
<ul>
  La mesure peut être :
  <li>en temps réel : via un système SPC (Maitrise statistiques des procédés) par exemple qui recueille la donnée en temps réel,</li>
  <li>périodique : toutes les heures, on compte le nombre de pièces réalisées,</li>
  <li>par échantillonnage ou sondage : pour mesurer la satisfaction client par exemple.</li>
</ul>
<p>
  Quel que soit le type de mesure ou sa périodicité, elle va être corrélée à un indicateur précis (ou KPI, Key Performance Indicator).
  Pour déterminer un bon indicateur, il doit être en lien, d'une manière ou d'une autre à la satisfaction du client.<br>
  On peut utiliser deux types d'indicateurs : Les indicateurs de processus et les indicateurs de résultats.
  Ils doivent nous dire si le résultat est conforme et si le processus a été correctement suivi.<br>
  On trouve généralement des indicateurs de résultats dans les entreprises mais les indicateurs de processus sont souvent manquants, bien que ce soit souvent le non-respect d'un processus qui entraîne une non-qualité ou un délai dépassé par exemple.
</p>
<div class="ia-sensei-example">
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${bulbMedium}"></path></svg>
  <div class="example-title">Par exemple :</div>
  Nous allons suivre une recette de cuisine pour la confection d'un gâteau. La recette va naturellement nous donner les bons indicateurs de processus que nous pourrons suivre tandis que le résultat de la confection du gâteau est le gâteau lui-même.
  Comme indicateurs de processus, nous pourrons donc choisir la température du four, la durée de cuisson, l'ordre des ingrédients, ... et les indicateurs de résultats pourront être
  la taille et la consistance du gâteau, un sondage auprès des invités, etc.
</div>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Les indicateurs, c'est fait. On s'y met ?</span>
</div>
<p>
  Une petite minute.<br>
  C'est une chose de savoir quel type de données est collecté. Mais vous devez être capable de
  prendre des décisions et d'agir rapidement lorsque quelque chose ne va pas. Le simple fait de regarder
  les données ne vous dira pas s'il y a un problème, sauf si vous êtes un expert en analyse de données.<br>
  C'est là que l'objectif entre en jeu.
</p>
<h2 class="basic-sub-title">
  Objectif SMART
</h2>
<p>
  La bonne définition d'un objectif est primordiale dans le cas d'indicateurs car les indicateurs
  sont souvent suivis quotidiennement.<br>
  <strong>SMART</strong> est un acronyme pour savoir comment établir et communiquer au sujet d'un objectif.<br>
</p>
<ul>
  <li>
    <strong>S</strong> pour Spécifique : L'objectif doit être concret, chiffré. Rester vague ne permet pas d'avoir
    une vision sur les résultats. Par exemple : "Réduire le nombre de défauts" n'est pas un objectif spécifique.<br>
    "Limiter le nombre de défauts à 12 par jour" : Cet objectif permet à tout le monde de savoir que la journée
    a été bonne ou à l'inverse que des améliorations peuvent être apportées.
  </li>
  <div class="ia-sensei-example">
    Prenons la température du four pendant la cuisson.
  </div>
  <li>
    <strong>M</strong> pour Mesurable : Toujours dans l'idée d'être précis, chacun doit être capable de mesurer
    l'écart à l'indicateur de manière précise.
  </li>
  <div class="ia-sensei-example">
    Un thermomètre fera l'affaire.
  </div>
  <li>
    <strong>A</strong> pour Atteignable : L'objectif doit être atteignable ou réalisable. L'ambition doit
    être mis de côté lors de la définition d'un objectif, un objectif ambitieux est l'ennemi du management
    quotidien de la performance. En effet, l'équipe risque d'être témoin de valeurs trop éloignées des objectifs.
  </li>
  <div class="ia-sensei-example">
    On peut dire que ... c'est du gâteau ?
  </div>
  <li>
    <strong>R</strong> pour Réaliste : L'objectif doit être en accord avec les ressources disponibles et donc
    réaliste.
  </li>
  <div class="ia-sensei-example">
    Vérifions les  paramètres de cuisson... On est bons !
  </div>
  <li>
    <strong>T</strong> pour Temps : L'objectif doit être atteint à un instant précis. Pour reprendre
    l'exemple "Réduire le nombre de défauts", il est impossible de savoir quand l'objectif est atteint.
    Dans le management quotidien de la performance, on donne une valeur à atteindre (ou à NE PAS atteindre)
    dans la journée.
  </li>
  <div class="ia-sensei-example">
    Durée indiquée dans la recette: 20 minutes.
  </div>
</ul>
<div class="ia-sensei-question">
  <div class="question-decoration"></div>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path d="${femaleQuestionMedium}"></path></svg>
  <span class="question">Et donc quand l'objectif est dépassé, je dois remonter un problème ?<span>
</div>
<p>
  Exactement. Un dépassement d'objectif peut signifier 2 choses : Soit l'objectif est mal défini,
  ce qui peut arrivé lors de la mise en place de nouveaux indicateurs, soit un problème est survenu.<br>
  Remontez un problème pour mener l'enquête et trouver la cause racine grâce aux outils proposés par Shizen.
</p>
`;

const INDICATORS_TITLE = 'Indicateurs et objectifs';

export default {
  INDICATORS,
  INDICATORS_TITLE,
};
